import React, { useState, useEffect, useRef, useCallback } from 'react';

const CountUp = ({ 
  end, 
  duration = 2000, 
  suffix = '', 
  prefix = '', 
  className = '',
  animationDelay = 0,
  direction = 'left' // 'left', 'right', 'up', 'down'
}) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  const animateCount = useCallback(() => {
    const startTime = Date.now();
    const startValue = 0;

    const updateCount = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = Math.floor(startValue + (end - startValue) * easeOutQuart);

      setCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(updateCount);
      }
    };

    requestAnimationFrame(updateCount);
  }, [end, duration]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true);
          
          // Délai pour l'animation d'entrée
          setTimeout(() => {
            animateCount();
          }, animationDelay);
        }
      },
      { 
        threshold: 0.3,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated, animationDelay, animateCount]);

  // Calcul de la direction d'animation
  const getTransformDirection = () => {
    switch (direction) {
      case 'left':
        return 'translateX(-100%)';
      case 'right':
        return 'translateX(100%)';
      case 'up':
        return 'translateY(100%)';
      case 'down':
        return 'translateY(-100%)';
      default:
        return 'translateX(-100%)';
    }
  };

  return (
    <span 
      ref={elementRef}
      className={`countup-element ${className}`}
      style={{
        display: 'inline-block',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateX(0) translateY(0)' : getTransformDirection(),
        transition: `all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${animationDelay}ms`,
        willChange: 'transform, opacity'
      }}
    >
      {prefix}
      <span 
        className="countup-number"
        style={{
          display: 'inline-block',
          fontWeight: 'bold',
          color: 'var(--primary, #4CAF50)',
          transition: 'all 0.3s ease'
        }}
      >
        {count}
      </span>
      {suffix}
    </span>
  );
};

// Composant wrapper responsive pour les statistiques
const CountUpGrid = ({ items = [], className = '' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div 
      ref={containerRef}
      className={`countup-grid ${className}`}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '2rem',
        width: '100%',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }}
    >
      {items.map((item, index) => (
        <div
          key={index}
          className="countup-item"
          style={{
            textAlign: 'center',
            padding: '1.5rem',
            background: 'var(--bg-white, #ffffff)',
            borderRadius: 'var(--radius-lg, 12px)',
            boxShadow: 'var(--shadow, 0 4px 6px rgba(0, 0, 0, 0.1))',
            border: '1px solid rgba(76, 175, 80, 0.1)',
            transition: 'all 0.3s ease',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateX(0)' : `translateX(${index % 2 === 0 ? '-50px' : '50px'})`,
            transitionDelay: `${index * 200}ms`
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = 'var(--shadow-hover, 0 8px 25px rgba(0, 0, 0, 0.15))';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'var(--shadow, 0 4px 6px rgba(0, 0, 0, 0.1))';
          }}
        >
          <div className="countup-value">
            <CountUp 
              end={item.value} 
              suffix={item.suffix || ''} 
              prefix={item.prefix || ''}
              direction={index % 2 === 0 ? 'left' : 'right'}
              animationDelay={index * 300}
            />
          </div>
          <div 
            className="countup-label"
            style={{
              marginTop: '0.5rem',
              fontSize: '0.9rem',
              color: 'var(--text-light, #666)',
              fontWeight: '400'
            }}
          >
            {item.label}
          </div>
        </div>
      ))}
    </div>
  );
};

// Composant pour les statistiques en ligne (responsive)
const CountUpRow = ({ items = [], className = '' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div 
      ref={containerRef}
      className={`countup-row ${className}`}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-around',
        alignItems: 'center',
        gap: '2rem',
        width: '100%',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }}
    >
      {items.map((item, index) => (
        <div
          key={index}
          className="countup-row-item"
          style={{
            textAlign: 'center',
            flex: '1 1 auto',
            minWidth: '120px',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateX(0)' : `translateX(${index % 2 === 0 ? '-30px' : '30px'})`,
            transition: `all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${index * 150}ms`
          }}
        >
          <div className="countup-value">
            <CountUp 
              end={item.value} 
              suffix={item.suffix || ''} 
              prefix={item.prefix || ''}
              direction={index % 2 === 0 ? 'left' : 'right'}
              animationDelay={index * 200}
            />
          </div>
          <div 
            className="countup-label"
            style={{
              marginTop: '0.5rem',
              fontSize: '0.85rem',
              color: 'var(--text-light, #666)',
              fontWeight: '400'
            }}
          >
            {item.label}
          </div>
        </div>
      ))}
    </div>
  );
};

// Composant d'exemple pour une section complète avec animations
const CountUpSection = ({ title, subtitle, items = [], layout = 'grid' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="countup-section"
      style={{
        padding: '4rem 0',
        background: 'var(--bg-white, #ffffff)',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
        transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }}
    >
      <div className="container">
        <div 
          className="section-header"
          style={{
            textAlign: 'center',
            marginBottom: '3rem',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s'
          }}
        >
          <h2 
            className="section-title"
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: '2.5rem',
              fontWeight: '700',
              color: 'var(--text-dark, #333)',
              marginBottom: '1rem',
              lineHeight: '1.2'
            }}
          >
            {title}
          </h2>
          {subtitle && (
            <p 
              className="section-subtitle"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '1.1rem',
                color: 'var(--text-light, #666)',
                lineHeight: '1.6',
                maxWidth: '600px',
                margin: '0 auto'
              }}
            >
              {subtitle}
            </p>
          )}
        </div>
        
        <div 
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.4s'
          }}
        >
          {layout === 'grid' ? (
            <CountUpGrid items={items} />
          ) : (
            <CountUpRow items={items} />
          )}
        </div>
      </div>
    </section>
  );
};

export default CountUp;
export { CountUpGrid, CountUpRow, CountUpSection }; 